import { useState, useRef } from "react"
import { useConfigContext, useLoadContext } from "shared/src/libs/contextLib/contextLib"
import { retrieveStickyValue } from "shared/src/libs/hooksLib/hooksLib"
import { useRecruiterPhoto } from "shared/src/libs/hooksLib/useRecruiterPhoto"
import { RecruiterPhoto } from "shared/src/components/RecruiterPhoto/RecruiterPhoto"
import { patchUser } from "shared/src/libs/apiLib/apiLib"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "shared/src/components/shadcn/components/ui/card"
import { Field, FieldLabel, FieldContent, FieldError } from "shared/src/components/shadcn/components/ui/field"
import { Input } from "shared/src/components/shadcn/components/ui/input"
import { AutoSaveInput, type AutoSaveStatus } from "shared/src/components/shadcn/components/atoms/inputs/AutoSaveInput"

interface DefaultsTabProps {
	showPhoto?: boolean
}

export function DefaultsTab({ showPhoto = true }: DefaultsTabProps) {
	const { localDefaultSettings } = useConfigContext()
	const { user, company, setUser } = useLoadContext()

	// Initialize field values
	const [recruitersName, setRecruitersName] = useState(
		user.displayName ||
			retrieveStickyValue({ ...localDefaultSettings.recruiterName, userId: user.id }) ||
			user.name ||
			""
	)
	const [recruitersCompany, setRecruitersCompany] = useState(
		user.displayCompanyName ||
			retrieveStickyValue({ ...localDefaultSettings.companyName, userId: user.id }) ||
			company.name ||
			""
	)
	const [phoneNumber, setPhoneNumber] = useState(
		user.phone || retrieveStickyValue({ ...localDefaultSettings.recruiterPhone, userId: user.id }) || ""
	)
	const [linkedInUrl, setLinkedInUrl] = useState(user.linkedInUrl || "")
	const [calendarLinkUrl, setCalendarLinkUrl] = useState(user.calendarLinkUrl ?? "")
	const [jobTitle, setJobTitle] = useState(
		user.jobTitle || retrieveStickyValue({ ...localDefaultSettings.jobTitle, userId: user.id }) || ""
	)

	// Track original values for comparison
	const originalValues = useRef({
		recruitersName,
		recruitersCompany,
		phoneNumber,
		linkedInUrl,
		calendarLinkUrl,
		jobTitle,
	})

	// Status tracking for each field
	const [nameStatus, setNameStatus] = useState<AutoSaveStatus>("idle")
	const [companyStatus, setCompanyStatus] = useState<AutoSaveStatus>("idle")
	const [phoneStatus, setPhoneStatus] = useState<AutoSaveStatus>("idle")
	const [linkedInStatus, setLinkedInStatus] = useState<AutoSaveStatus>("idle")
	const [calendarStatus, setCalendarStatus] = useState<AutoSaveStatus>("idle")
	const [jobTitleStatus, setJobTitleStatus] = useState<AutoSaveStatus>("idle")

	const [recruiterPhoto, setRecruiterPhoto, isPhotoLoading] = useRecruiterPhoto()

	const recruitersEmail = user.email || user.miscData?.split("#")[1] || ""

	// Generic save handler factory
	function createSaveHandler(fieldName: keyof typeof originalValues.current) {
		return async (value: string): Promise<void> => {
			const trimmedValue = value.trim()
			if (trimmedValue === originalValues.current[fieldName]) return

			await patchUser({
				userId: user.id,
				email: user.email,
				clientId: company.id,
				...(fieldName === "recruitersName" && { displayName: trimmedValue }),
				...(fieldName === "recruitersCompany" && { displayCompanyName: trimmedValue }),
				...(fieldName === "phoneNumber" && { phone: trimmedValue }),
				...(fieldName === "jobTitle" && { jobTitle: trimmedValue }),
				...(fieldName === "linkedInUrl" && { linkedInUrl: trimmedValue }),
				...(fieldName === "calendarLinkUrl" && { calendarLinkUrl: trimmedValue }),
			})

			// Update user context
			setUser({
				...user,
				...(fieldName === "phoneNumber" && { phone: trimmedValue }),
				...(fieldName === "jobTitle" && { jobTitle: trimmedValue }),
				...(fieldName === "linkedInUrl" && { linkedInUrl: trimmedValue }),
				...(fieldName === "recruitersName" && { displayName: trimmedValue }),
				...(fieldName === "recruitersCompany" && { displayCompanyName: trimmedValue }),
				...(fieldName === "calendarLinkUrl" && { calendarLinkUrl: trimmedValue }),
			})

			// Update original value
			originalValues.current[fieldName] = trimmedValue
		}
	}

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>Default CV Display Values</CardTitle>
					<CardDescription>
						Set your default recruiter contact information for formatted CVs. Changes save automatically.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* Email - read only */}
					<Field orientation="horizontal" className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start">
						<FieldLabel htmlFor="recruiter-email">Recruiter's Email</FieldLabel>
						<FieldContent className="max-w-md">
							<Input id="recruiter-email" value={recruitersEmail} disabled className="bg-muted" type="email" />
						</FieldContent>
					</Field>

					{/* Recruiter's Name */}
					<Field
						orientation="horizontal"
						className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start"
						data-invalid={nameStatus === "error"}>
						<FieldLabel htmlFor="recruiter-name">Recruiter's Name</FieldLabel>
						<FieldContent className="max-w-md space-y-1.5">
							<AutoSaveInput
								id="recruiter-name"
								value={recruitersName}
								onChange={setRecruitersName}
								onSave={createSaveHandler("recruitersName")}
								status={nameStatus}
								onStatusChange={setNameStatus}
								placeholder="Recruiter's Name"
							/>
							{nameStatus === "error" && <FieldError>Failed to save. Please try again.</FieldError>}
						</FieldContent>
					</Field>

					{/* Recruiter's Company */}
					<Field
						orientation="horizontal"
						className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start"
						data-invalid={companyStatus === "error"}>
						<FieldLabel htmlFor="recruiter-company">Recruiter's Company</FieldLabel>
						<FieldContent className="max-w-md space-y-1.5">
							<AutoSaveInput
								id="recruiter-company"
								value={recruitersCompany}
								onChange={setRecruitersCompany}
								onSave={createSaveHandler("recruitersCompany")}
								status={companyStatus}
								onStatusChange={setCompanyStatus}
								placeholder="Recruiter's Company"
							/>
							{companyStatus === "error" && <FieldError>Failed to save. Please try again.</FieldError>}
						</FieldContent>
					</Field>

					{/* Phone Number */}
					<Field
						orientation="horizontal"
						className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start"
						data-invalid={phoneStatus === "error"}>
						<FieldLabel htmlFor="phone-number">Phone Number</FieldLabel>
						<FieldContent className="max-w-md space-y-1.5">
							<AutoSaveInput
								id="phone-number"
								value={phoneNumber}
								onChange={setPhoneNumber}
								onSave={createSaveHandler("phoneNumber")}
								status={phoneStatus}
								onStatusChange={setPhoneStatus}
								placeholder="Phone Number"
								type="tel"
							/>
							{phoneStatus === "error" && <FieldError>Failed to save. Please try again.</FieldError>}
						</FieldContent>
					</Field>

					{/* LinkedIn URL */}
					<Field
						orientation="horizontal"
						className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start"
						data-invalid={linkedInStatus === "error"}>
						<FieldLabel htmlFor="linkedin-url">LinkedIn URL</FieldLabel>
						<FieldContent className="max-w-md space-y-1.5">
							<AutoSaveInput
								id="linkedin-url"
								value={linkedInUrl}
								onChange={setLinkedInUrl}
								onSave={createSaveHandler("linkedInUrl")}
								status={linkedInStatus}
								onStatusChange={setLinkedInStatus}
								placeholder="LinkedIn URL"
								type="url"
							/>
							{linkedInStatus === "error" && <FieldError>Failed to save. Please try again.</FieldError>}
						</FieldContent>
					</Field>

					{/* Calendar Booking Link */}
					<Field
						orientation="horizontal"
						className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start"
						data-invalid={calendarStatus === "error"}>
						<FieldLabel htmlFor="calendar-link">Calendar Booking Link</FieldLabel>
						<FieldContent className="max-w-md space-y-1.5">
							<AutoSaveInput
								id="calendar-link"
								value={calendarLinkUrl}
								onChange={setCalendarLinkUrl}
								onSave={createSaveHandler("calendarLinkUrl")}
								status={calendarStatus}
								onStatusChange={setCalendarStatus}
								placeholder="Calendar Booking Link"
								type="url"
							/>
							{calendarStatus === "error" && <FieldError>Failed to save. Please try again.</FieldError>}
						</FieldContent>
					</Field>

					{/* Job Title */}
					<Field
						orientation="horizontal"
						className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start"
						data-invalid={jobTitleStatus === "error"}>
						<FieldLabel htmlFor="job-title">Job Title</FieldLabel>
						<FieldContent className="max-w-md space-y-1.5">
							<AutoSaveInput
								id="job-title"
								value={jobTitle}
								onChange={setJobTitle}
								onSave={createSaveHandler("jobTitle")}
								status={jobTitleStatus}
								onStatusChange={setJobTitleStatus}
								placeholder="Job Title"
							/>
							{jobTitleStatus === "error" && <FieldError>Failed to save. Please try again.</FieldError>}
						</FieldContent>
					</Field>

					{/* Display Photo */}
					{showPhoto && (
						<Field orientation="horizontal" className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-start">
							<FieldLabel htmlFor="recruiter-photo">Display Photo</FieldLabel>
							<FieldContent className="max-w-md">
								<div className="adminTab">
									<RecruiterPhoto
										recruiterPhoto={recruiterPhoto}
										setRecruiterPhoto={setRecruiterPhoto}
										loading={isPhotoLoading}
									/>
								</div>
							</FieldContent>
						</Field>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
